import Vue from 'utils/vueUtils';
import Invites from '../collections/Invites';
import Invite from '../models/Invite';
import { baseApi } from 'core/api';

const state = () => ({
    _invitation: null,
    _invitations: null,
    _rolesPermissions: null,
    _organizationPermissions: null,
    _organizationPermissionsArray: [],
});

const getters = {
    invitation: (state) => state._invitation,
    invitations: (state) => state._invitations,
    rolesPermissions: (state) => state._rolesPermissions,
    organizationPermissions: (state) => state._organizationPermissions,
    organizationPermissionsArray: (state) => Object.values(state._organizationPermissions || {}),
};

const actions = {
    getOrganizationInvitesById(id) {
        return baseApi.get(`core/organization/invites/${id}`).then((response) => {
            this._setInvitationLoaded(response.data);
            return response;
        });
    },

    getRoles() {
        return baseApi.get('core/roles').then((response) => {
            this._setRolesPermissions(response.data);
            return response;
        });
    },

    getOrganizationInvites(retry) {
        if (retry === true) {
            this._setInvitationsLoadRetry();
        }

        return baseApi
            .get('core/organization/invites')
            .then((response) => {
                const invites = new Invites([
                    ...response.data.invitations,
                    ...response.data.invitation_requests,
                ]);

                this._setInvitationsLoaded(invites.models);

                return response;
            })
            .catch(() => {
                this._setInvitationsLoadError();
            });
    },

    updateOrganizationInvites(form) {
        return form.post('core/organization/invites').then((response) => {
            response.data.forEach((invite) => {
                if (form.update) {
                    this._setInvitationRevoked(form.id);
                }

                this._setInvitationSent(new Invite(invite));
            });

            return response;
        });
    },

    updateOrganizationInvitesById({ id, form }) {
        return form.post(`core/organization/invites/${id}`).then((response) => {
            this._setInvitationUpdated({
                id: id,
                data: new Invite(response.data),
            });
            return response;
        });
    },

    deleteOrganizationInvitesById({ id, form }) {
        return form.delete(`core/organization/invites/${id}`).then((response) => {
            this._setInvitationRevoked(id);
            return response;
        });
    },

    updateHubspotExtensionUserInvitationRejectById(id) {
        return baseApi
            .post(`core/hubspot/extension/user/invitation/reject/${id}`)
            .then((response) => {
                this._setInvitationRevoked(id);

                return response.data;
            });
    },

    inviteLoading(data) {
        this._setInvitateLoading(data);
    },

    _setUserMeSignal() {
        this._invitation = null;
    },

    // Private methods
    _setInvitateLoading({ invite, loading }) {
        this._invitations = this._invitations.map((item) => {
            if (item.id === invite.id) {
                item.loading = loading;
            }
            return item;
        });
    },

    _setInvitationLoaded(data) {
        this._invitation = new Invite(data);
    },

    _setInvitationsLoadRetry() {
        this._invitations = null;
    },

    _setInvitationsLoadError() {
        this._invitations = false;
    },

    _setInvitationsLoaded(data) {
        this._invitations = data;
    },

    _setInvitationSent(data) {
        if (Array.isArray(this._invitations)) {
            this._invitations.push(data);
        } else {
            this._invitations = [data];
        }
    },

    _setInvitationRevoked(id) {
        this._invitations = this._invitations.filter((o) => o.id !== id);
    },

    _setInvitationUpdated({ id, data }) {
        let index = this._invitations.findIndex((o) => o.id === id);
        Vue.set(this._invitations, index, data);
    },

    _setRolesPermissions(data) {
        let rolesPermissions = {};
        let organizationPermissions = {};

        for (let key in data) {
            const item = data[key];
            const availablePermissionsArray = Object.values(item.available_permissions).map(
                (item) => {
                    return {
                        ...item,
                        type: 'available',
                    };
                }
            );

            const unavailablePermissionsArray = Object.values(item.unavailable_permissions).map(
                (item) => {
                    return {
                        ...item,
                        type: 'unavailable',
                    };
                }
            );

            const organizationPermissionsArray = Object.values(item.organization_permissions).map(
                (item) => ({ ...item })
            );

            let array = [...availablePermissionsArray, ...unavailablePermissionsArray].map(
                (item) => {
                    if (item.key === 'billing') item.description = 'Can manage Billing';
                    if (item.key === 'broadcasts') item.description = 'Can access Broadcasts';
                    if (item.key === 'dataAndSecurity')
                        item.description = 'Can manage Data & Security';
                    if (item.key === 'inbox') item.description = 'Can manage Inboxes';
                    if (item.key === 'members') item.description = 'Can manage Members';
                    if (item.key === 'reportingAndAnalytics')
                        item.description = 'Can access Analytics';
                    if (item.key === 'triggers') item.description = 'Can access Triggers';
                    if (item.key === 'textBot') item.description = 'Can access AI Agent';
                    if (item.key === 'manualOptIn') item.description = 'Can access Manual Opt-In';
                    if (item.key === 'phoneChecker') item.description = 'Can access PhoneCheckr';
                    if (Object.values(item)[0].key === 'contactAccess') {
                        item.key = 'contactAccess';
                        item.label = 'Contact Access';
                        item.description = 'Can access Contacts';
                    }
                    if (item.key === 'campaigns') item.description = 'Can access Campaigns';
                    if (item.key === 'contactCreate') item.description = 'Can create Contacts';
                    if (item.key === 'transferConversation')
                        item.description = 'Can transfer Conversations';
                    if (item.key === 'cannedMessage') item.description = 'Can manage Saved Replies';

                    return item;
                }
            );

            const sort = {
                broadcasts: 0,
                campaigns: 1,
                textBot: 2,
                triggers: 3,
                reportingAndAnalytics: 4,
                dataAndSecurity: 5,
                inbox: 6,
                members: 7,
                billing: 8,
                manualOptIn: 9,
                phoneChecker: 10,
                contactCreate: 11,
                transferConversation: 12,
                cannedMessage: 13,
                contactAccess: 14,
            };

            const organizationPermissionsMergeArray = array
                .map((item) => {
                    const organizationPermission = organizationPermissionsArray.find(
                        (orgItem) => orgItem.key === item.key
                    );

                    if (organizationPermission) {
                        return {
                            ...item,
                            default: organizationPermission.default,
                        };
                    }

                    return item;
                })
                .sort((a, b) => {
                    const aValue = sort[a.key];
                    const bValue = sort[b.key];

                    return aValue - bValue;
                });

            rolesPermissions[key] = {
                key: item.key,
                label: item.label,
                permissions: array,
            };

            organizationPermissions[key] = {
                key: item.key,
                label: item.label,
                permissions: organizationPermissionsMergeArray,
            };
        }

        this._rolesPermissions = rolesPermissions;
        this._organizationPermissions = organizationPermissions;
    },
};

export default {
    state,
    getters,
    actions,
};
